import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import api, { fileUrl, img } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { SectionTitle, Pill } from "@/components/Bits";

export default function About() {
  const { t, L } = useLang();
  const [series, setSeries] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    api.get("/series").then((r) => setSeries(r.data)).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
      <div className="max-w-3xl">
        <SectionTitle tag="ABOUT · 放空简介" title={t("about_title")} />
        <p className="text-lg text-black/70 leading-relaxed -mt-4">{t("about_intro")}</p>
      </div>

      <div className="mt-14 space-y-20">
        {series.map((s, idx) => (
          <motion.div
            key={s.id}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="grid lg:grid-cols-2 gap-8 items-center"
          >
            <div className={idx % 2 ? "lg:order-2" : ""}>
              <img src={img(s.cover_image)} alt={s.title_en} className="rounded-2xl fk-border-thick fk-shadow w-full h-80 object-cover" />
              {s.images?.length > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {s.images.slice(0, 3).map((img, i) => (
                    <img key={i} src={fileUrl(img)} alt="" className="rounded-xl fk-border h-24 w-full object-cover" />
                  ))}
                </div>
              )}
            </div>
            <div className={idx % 2 ? "lg:order-1" : ""}>
              <Pill color="#00E5FF">{t("series_label")} · 0{idx + 1}</Pill>
              <h3 className="font-display text-3xl sm:text-4xl mt-4">{L(s.title_zh, s.title_en)}</h3>
              <p className="mt-4 text-black/70 leading-relaxed">{L(s.intro_zh, s.intro_en)}</p>
              {s.whatsapp && (
                <a
                  href={`https://wa.me/${s.whatsapp}`}
                  target="_blank" rel="noreferrer"
                  data-testid={`series-whatsapp-${idx}`}
                  className="mt-6 inline-flex items-center gap-2 fk-border rounded-full px-5 py-2.5 font-semibold bg-[#25D366] text-white fk-lift"
                >
                  <MessageCircle size={18} /> {t("whatsapp_us")}
                </a>
              )}
            </div>
          </motion.div>
        ))}
        {series.length === 0 && <p className="text-black/50 font-mono-fk">暂无系列 / No series yet.</p>}
      </div>

      {partners.length > 0 && (
        <div className="mt-24">
          <div className="font-mono-fk text-xs tracking-widest text-[#FF5757] mb-2">PARTNERS · 合作单位</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-8">{t("partners_title")}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {partners.map((p, i) => (
              <motion.a
                key={p.id} href={p.url || "#"} target={p.url ? "_blank" : undefined} rel="noreferrer"
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                data-testid={`partner-${p.id}`}
                className="bg-white fk-border-thick rounded-2xl overflow-hidden fk-lift block"
              >
                {p.logo && <img src={fileUrl(p.logo)} alt={p.name} className="w-full h-28 object-cover border-b-2 border-black" />}
                <div className="p-4 font-semibold text-center">{p.name}</div>
              </motion.a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
